import { useEffect, useState } from "react";
import api from "@/lib/api";
import { fmtL, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { downloadFile } from "@/pages/_hr/download";
import { DownloadIcon, GearIcon } from "@phosphor-icons/react";

function Table({ title, cols, rows, testid }) {
  return (
    <div className="bg-card border border-border/60 rounded-lg overflow-hidden" data-testid={testid}>
      <div className="p-3 border-b border-border/60"><h4 className="font-heading text-sm uppercase tracking-wider text-agro-green">{title}</h4></div>
      <table className="w-full text-xs">
        <thead className="bg-sidebar"><tr className="text-left text-[10px] uppercase tracking-wider text-text-muted">
          {cols.map((c) => <th key={c.k} className={`p-2 ${c.money || c.num ? "text-right" : ""}`}>{c.l}</th>)}
        </tr></thead>
        <tbody>
          {rows.length === 0 ? <tr><td colSpan={cols.length} className="p-4 text-center text-text-muted">Sin datos</td></tr>
            : rows.map((r, i) => (
              <tr key={i} className="border-t border-border/40">
                {cols.map((c) => <td key={c.k} className={`p-2 ${c.money || c.num ? "text-right" : ""}`}>{c.money ? fmtL(r[c.k]) : (r[c.k] ?? "—")}</td>)}
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  );
}

// Secciones adicionales del reporte de viáticos (12.5) + export Excel/PDF.
export default function ExpenseReportExtras({ data, params }) {
  const clean = Object.fromEntries(Object.entries(params || {}).filter(([, v]) => v));
  const rp = data.reembolsos_pendientes || { count: 0, total: 0, por_solicitante: [] };
  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <button onClick={() => downloadFile("/expenses/report/export", "reporte_viaticos.xlsx", { ...clean, formato: "xlsx" })} data-testid="exp-export-xlsx"
          className="text-xs px-3 py-1.5 border border-agro-green/40 text-agro-green rounded-md flex items-center gap-1"><DownloadIcon size={14} /> Excel</button>
        <button onClick={() => downloadFile("/expenses/report/export", "reporte_viaticos.pdf", { ...clean, formato: "pdf" })} data-testid="exp-export-pdf"
          className="text-xs px-3 py-1.5 border border-agro-yellow/40 text-agro-yellow rounded-md flex items-center gap-1"><DownloadIcon size={14} /> PDF</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Table title={`Reembolsos pendientes (${rp.count} · ${fmtL(rp.total)})`} testid="rep-pendientes"
          cols={[{ k: "solicitante", l: "Solicitante" }, { k: "count", l: "#", num: true }, { k: "total", l: "Monto", money: true }]} rows={rp.por_solicitante} />
        <Table title="Adelantos por RTC vs liquidaciones" testid="rep-adelantos"
          cols={[{ k: "beneficiario_nombre", l: "Beneficiario" }, { k: "entregado", l: "Entregado", money: true }, { k: "liquidado", l: "Liquidado", money: true }, { k: "saldo", l: "Saldo", money: true }]}
          rows={data.adelantos_por_rtc || []} />
      </div>
      <Table title="Comparativo por período" testid="rep-periodos"
        cols={[{ k: "periodo", l: "Período" }, { k: "count", l: "#", num: true }, { k: "total", l: "Total", money: true }, { k: "aprobado", l: "Aprobado", money: true }, { k: "pendiente_reembolso", l: "Pend. reembolso", money: true }]}
        rows={data.comparativo_periodos || []} />
      <Table title="Distribución (proveedor / partida / categoría / línea)" testid="rep-distribucion"
        cols={[{ k: "tipo", l: "Tipo" }, { k: "nombre", l: "Destino" }, { k: "count", l: "#", num: true }, { k: "monto", l: "Monto", money: true }]}
        rows={(data.por_distribucion || []).slice().sort((a, b) => b.monto - a.monto)} />
    </div>
  );
}

// Configuración contable de viáticos (contrapartida tarjeta de empresa).
export function ExpensesConfigPanel({ accounts }) {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/expenses/config").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  if (!cfg) return null;
  const save = async () => {
    try { await api.put("/expenses/config", cfg); toast.success("Configuración guardada"); } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="bg-card border border-border/60 rounded-lg p-4 space-y-3" data-testid="exp-config">
      <h4 className="font-heading text-sm uppercase tracking-wider text-agro-green flex items-center gap-2"><GearIcon size={14} /> Contabilización de gastos con tarjeta de la empresa</h4>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm items-end">
        <div>
          <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1 block">Contrapartida</label>
          <select value={cfg.tarjeta_contrapartida} onChange={(e) => setCfg({ ...cfg, tarjeta_contrapartida: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
            <option value="pasivo">Pasivo: 2115 Tarjetas de crédito por pagar</option>
            <option value="banco">Cuenta de tesorería (banco / tarjeta)</option>
          </select>
        </div>
        {cfg.tarjeta_contrapartida === "banco" && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1 block">Cuenta de tesorería</label>
            <select value={cfg.tarjeta_bank_account_id} onChange={(e) => setCfg({ ...cfg, tarjeta_bank_account_id: e.target.value })} className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
              <option value="">— Seleccionar —</option>
              {accounts.map((a) => <option key={a.id} value={a.id}>{a.codigo} · {a.nombre}</option>)}
            </select>
          </div>
        )}
        <button onClick={save} className="px-4 py-2 text-sm bg-agro-green text-black font-medium rounded-md">Guardar</button>
      </div>
      <p className="text-[11px] text-text-muted">Efectivo propio → Cr 2117 Reembolsos por pagar a empleados (se paga desde "Reembolsar" o en la planilla). Contra adelanto → Cr 1107 Anticipos.</p>
    </div>
  );
}
